import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'

const Dashboard = () => {
  const { user, logout, updateProfile, regenerateApiKey } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    name: user?.name || '',
    webhook: user?.webhook || '',
    eth: user?.eth || '',
  })
  const [isUpdating, setIsUpdating] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [updateMessage, setUpdateMessage] = useState('')

  const handleEdit = () => {
    setIsEditing(true)
    setEditData({
      name: user?.name || '',
      webhook: user?.webhook || '',
      eth: user?.eth || '',
    })
    setUpdateMessage('')
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditData({
      name: user?.name || '',
      webhook: user?.webhook || '',
      eth: user?.eth || '',
    })
    setUpdateMessage('')
  }

  const handleSave = async () => {
    setIsUpdating(true)
    setUpdateMessage('')

    try {
      await updateProfile(editData)
      setIsEditing(false)
      setUpdateMessage('Profile updated successfully!')
      setTimeout(() => setUpdateMessage(''), 3000)
    } catch (error) {
      setUpdateMessage('Failed to update profile: ' + error.message)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleRegenerateApiKey = async () => {
    if (
      !confirm(
        'Are you sure you want to regenerate your API key? This will invalidate the current key.'
      )
    ) {
      return
    }

    setIsRegenerating(true)
    try {
      await regenerateApiKey()
      setUpdateMessage('API key regenerated successfully!')
      setTimeout(() => setUpdateMessage(''), 3000)
    } catch (error) {
      setUpdateMessage('Failed to regenerate API key: ' + error.message)
    } finally {
      setIsRegenerating(false)
    }
  }

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text)
    setUpdateMessage('Copied to clipboard!')
    setTimeout(() => setUpdateMessage(''), 2000)
  }

  const truncateAddress = address => {
    if (!address) return ''
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <ProfileSection
            user={user}
            isEditing={isEditing}
            editData={editData}
            setEditData={setEditData}
            handleEdit={handleEdit}
            handleCancel={handleCancel}
            handleSave={handleSave}
            isUpdating={isUpdating}
            isRegenerating={isRegenerating}
            handleRegenerateApiKey={handleRegenerateApiKey}
            copyToClipboard={copyToClipboard}
            truncateAddress={truncateAddress}
          />
        )
      case 'accepts':
        return <AcceptsSection user={user} />
      case 'customers':
        return <CustomersSection user={user} />
      case 'payments':
        return <PaymentsSection user={user} />
      default:
        return null
    }
  }

  return (
    <div className="h-screen bg-gray-50">
      <div className="flex h-full overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-64 bg-white shadow-lg flex flex-col flex-shrink-0">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <img src="/logo-nobg.png" alt="ZPayNow" className="w-8 h-8" />
              <h1 className="text-2xl font-bold text-gray-900">ZPayNow</h1>
            </div>
            <p
              className="text-sm text-gray-600 mt-1 truncate"
              title={user?.name || user?.address}
            >
              {user?.name || truncateAddress(user?.address)}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-3 ${
                activeTab === 'profile'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:translate-x-1'
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Profile
            </button>

            <button
              onClick={() => setActiveTab('accepts')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-3 ${
                activeTab === 'accepts'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:translate-x-1'
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Accepts
            </button>

            <button
              onClick={() => setActiveTab('customers')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-3 ${
                activeTab === 'customers'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:translate-x-1'
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              Customers
            </button>

            <button
              onClick={() => setActiveTab('payments')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-3 ${
                activeTab === 'payments'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:translate-x-1'
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
              Payments
            </button>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={logout}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Sign Out
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-7xl mx-auto h-full">
              {/* Update Message */}
              {updateMessage && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    updateMessage.includes('Failed') ||
                    updateMessage.includes('error')
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : 'bg-green-100 text-green-700 border border-green-200'
                  }`}
                >
                  {updateMessage}
                </div>
              )}

              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Profile Section Component
const ProfileSection = ({
  user,
  isEditing,
  editData,
  setEditData,
  handleEdit,
  handleCancel,
  handleSave,
  isUpdating,
  isRegenerating,
  handleRegenerateApiKey,
  copyToClipboard,
  truncateAddress,
}) => {
  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Profile Information
          </h2>
          {!isEditing && (
            <button
              onClick={handleEdit}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg"
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-6 space-y-6 flex-1 overflow-y-auto">
        {/* Ethereum Address (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ethereum Address
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 font-mono">
              {user?.address}
            </div>
            <button
              onClick={() => copyToClipboard(user?.address)}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="Copy to clipboard"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Display Name
          </label>
          {isEditing ? (
            <input
              type="text"
              value={editData.name}
              onChange={e => setEditData({ ...editData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              placeholder="Enter your display name"
            />
          ) : (
            <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
              {user?.name || 'Not set'}
            </div>
          )}
        </div>

        {/* Webhook URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Webhook URL
          </label>
          {isEditing ? (
            <input
              type="url"
              value={editData.webhook}
              onChange={e =>
                setEditData({ ...editData, webhook: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              placeholder="https://your-domain.com/webhook"
            />
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 break-all">
                {user?.webhook || 'Not set'}
              </div>
              {user?.webhook && (
                <button
                  onClick={() => copyToClipboard(user?.webhook)}
                  className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                  title="Copy to clipboard"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* ETH Payment Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ETH Payment Address
          </label>
          {isEditing ? (
            <input
              type="text"
              value={editData.eth}
              onChange={e => setEditData({ ...editData, eth: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono transition-all duration-200"
              placeholder="0x..."
            />
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-mono">
                {user?.eth || 'Not set'}
              </div>
              {user?.eth && (
                <button
                  onClick={() => copyToClipboard(user?.eth)}
                  className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                  title="Copy to clipboard"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* API Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            API Key
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-mono">
              {user?.apikey
                ? `${user.apikey.slice(0, 12)}...${user.apikey.slice(-4)}`
                : 'Not available'}
            </div>
            <button
              onClick={() => copyToClipboard(user?.apikey)}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="Copy full API key"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
            <button
              onClick={handleRegenerateApiKey}
              disabled={isRegenerating}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors duration-200"
            >
              {isRegenerating ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Use this API key to authenticate requests to the ZeroPay API
          </p>
        </div>

        {/* Edit Actions */}
        {isEditing && (
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={isUpdating}
              className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg"
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isUpdating}
              className="bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Accepts Section Component
const AcceptsSection = ({ user }) => {
  const { updateProfile } = useAuth()
  const [chains, setChains] = useState([])
  const [enabledTokens, setEnabledTokens] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saveMessage, setSaveMessage] = useState('')

  const API_BASE = 'https://api.zpaynow.com'

  // Fetch payment options from API
  const fetchPaymentOptions = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await fetch(API_BASE)
      if (!response.ok) {
        throw new Error('Failed to fetch payment options')
      }
      const data = await response.json()

      console.log('API Response:', data)
      console.log('Data type:', typeof data, 'Is array:', Array.isArray(data))

      // Store the full chain data for displaying commission and estimation info
      if (Array.isArray(data)) {
        setChains(data)
      }

      // Initialize enabled tokens from user data
      setEnabledTokens(user?.chains || [])
    } catch (err) {
      setError(err.message)
      console.error('Error fetching payment options:', err)
    } finally {
      setIsLoading(false)
    }
  }, [user?.chains])

  useEffect(() => {
    fetchPaymentOptions()
  }, [fetchPaymentOptions])

  const toggleToken = tokenIdentity => {
    setEnabledTokens(prev => {
      if (prev.includes(tokenIdentity)) {
        return prev.filter(id => id !== tokenIdentity)
      } else {
        return [...prev, tokenIdentity]
      }
    })
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveMessage('')

    try {
      const token = localStorage.getItem('zeropay_token')
      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${API_BASE}/merchants/info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          chains: enabledTokens,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to update payment options')
      }

      const data = await response.json()

      // Update user context with response data (includes updated chains from backend)
      await updateProfile({ chains: enabledTokens })

      // If backend returns new chains data, update the chains state
      if (data.chains) {
        setEnabledTokens(data.chains)
      }

      // Refetch payment options to get any new chains from backend
      await fetchPaymentOptions()

      setSaveMessage('Payment options saved successfully!')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (err) {
      setSaveMessage(`Error: ${err.message}`)
      console.error('Error saving payment options:', err)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment options...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow h-full flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="font-semibold mb-2">Error loading payment options</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-900">Payment Options</h2>
        <p className="text-sm text-gray-600 mt-1">
          Choose which tokens you want to accept
        </p>
      </div>

      {saveMessage && (
        <div
          className={`mx-6 mt-4 p-3 rounded-lg text-sm ${
            saveMessage.includes('Error')
              ? 'bg-red-100 text-red-700 border border-red-200'
              : 'bg-green-100 text-green-700 border border-green-200'
          }`}
        >
          {saveMessage}
        </div>
      )}

      <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
        {chains.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <p>No payment options available</p>
          </div>
        )}

        {chains.map(chain => {
          const chainTokens = Object.values(chain.tokens || {})
          const enabledCount = chainTokens.filter(t =>
            enabledTokens.includes(t.identity)
          ).length

          return (
            <div
              key={chain.name}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-900">
                      {chain.name}
                    </h3>
                    {chain.name === 'Sepolia' && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300 rounded">
                        Testnet
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-600">
                    <span>⏱ Estimation: ~{chain.estimation}s</span>
                    <span>
                      💰 Commission: {chain.commission}% ($
                      {chain.commission_min / 100}-${chain.commission_max / 100}
                      )
                    </span>
                    <span className="text-gray-500">
                      {enabledCount}/{chainTokens.length} enabled
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {chainTokens.map(token => {
                  const isEnabled = enabledTokens.includes(token.identity)
                  return (
                    <button
                      key={token.identity}
                      onClick={() => toggleToken(token.identity)}
                      className={`relative px-4 py-3 rounded-lg border-2 transition-all duration-200 group ${
                        isEnabled
                          ? 'border-primary-500 bg-primary-600 text-white shadow-md hover:-translate-y-0.5 hover:shadow-lg'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:shadow-md hover:-translate-y-0.5'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">
                          {token.name}
                        </span>
                        <div
                          className={`w-4 h-4 rounded-full flex items-center justify-center ${
                            isEnabled ? 'bg-white' : 'bg-gray-200'
                          }`}
                        >
                          {isEnabled && (
                            <svg
                              className="w-3 h-3 text-primary-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div
                        className={`text-xs mt-1 ${isEnabled ? 'text-primary-100' : 'text-gray-500'}`}
                      >
                        {isEnabled ? 'Open' : 'Close'}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-6 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
        >
          {isSaving ? 'Saving...' : 'Save Payment Options'}
        </button>
      </div>
    </div>
  )
}

// Customers Section Component
const CustomersSection = ({ user }) => {
  const [customers, setCustomers] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const API_BASE = 'https://api.zpaynow.com'
  const itemsPerPage = 10

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setIsLoading(true)
        const token = localStorage.getItem('zeropay_token')
        if (!token) {
          throw new Error('Not authenticated')
        }

        // Don't include page parameter for first page
        const url =
          currentPage === 1
            ? `${API_BASE}/merchants/customers`
            : `${API_BASE}/merchants/customers?page=${currentPage}`

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch customers')
        }

        const data = await response.json()
        setCustomers(data.items || [])
        setTotalItems(data.total || 0)
        setError(null)
      } catch (err) {
        setError(err.message)
        console.error('Error fetching customers:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCustomers()
  }, [currentPage])

  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)

  const truncateAddress = address => {
    if (!address) return ''
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow h-full flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="font-semibold mb-2">Error loading customers</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-900">Customers</h2>
        <p className="text-sm text-gray-600 mt-1">View all your customers</p>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ETH Address
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {customers.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                  No customers found
                </td>
              </tr>
            ) : (
              customers.map(customer => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    #{customer.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {customer.account || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                    {customer.eth ? (
                      <span title={customer.eth}>
                        {truncateAddress(customer.eth)}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
            <span className="font-medium">{endIndex}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page
                if (totalPages <= 5) {
                  page = i + 1
                } else if (currentPage <= 3) {
                  page = i + 1
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i
                } else {
                  page = currentPage - 2 + i
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                )
              })}
            </div>
            <button
              onClick={() =>
                setCurrentPage(prev => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Payments Section Component
const PaymentsSection = ({ user }) => {
  const [sessions, setSessions] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const API_BASE = 'https://api.zpaynow.com'
  const itemsPerPage = 10

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setIsLoading(true)
        const token = localStorage.getItem('zeropay_token')
        if (!token) {
          throw new Error('Not authenticated')
        }

        // Don't include page parameter for first page
        const url =
          currentPage === 1
            ? `${API_BASE}/merchants/sessions`
            : `${API_BASE}/merchants/sessions?page=${currentPage}`

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch sessions')
        }

        const data = await response.json()
        setSessions(data.items || [])
        setTotalItems(data.total || 0)
        setError(null)
      } catch (err) {
        setError(err.message)
        console.error('Error fetching sessions:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchSessions()
  }, [currentPage])

  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)

  const getSessionStatus = session => {
    if (session.deposit !== null) {
      return 'success'
    }

    const expiredAt = new Date(session.expired_at)
    const now = new Date()

    if (now > expiredAt) {
      return 'expired'
    }

    return 'pending'
  }

  const getStatusColor = status => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800'
      case 'expired':
        return 'bg-gray-100 text-gray-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = dateString => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatAmount = amount => {
    if (!amount) return '-'
    // Amount is in cents, convert to dollars
    return `$${(amount / 100).toFixed(2)}`
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payments...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow h-full flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="font-semibold mb-2">Error loading payments</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-900">
          Payment Sessions
        </h2>
        <p className="text-sm text-gray-600 mt-1">View all payment sessions</p>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Webhook
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Updated
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sessions.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                  No payment sessions found
                </td>
              </tr>
            ) : (
              sessions.map(session => {
                const status = getSessionStatus(session)
                return (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      #{session.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      #{session.customer || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatAmount(session.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(status)}`}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {session.sent ? (
                        <span className="text-green-600">✓ Sent</span>
                      ) : (
                        <span className="text-gray-400">✗ Not sent</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(session.updated_at)}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
            <span className="font-medium">{endIndex}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page
                if (totalPages <= 5) {
                  page = i + 1
                } else if (currentPage <= 3) {
                  page = i + 1
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i
                } else {
                  page = currentPage - 2 + i
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                )
              })}
            </div>
            <button
              onClick={() =>
                setCurrentPage(prev => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
